import { Grid } from "@mui/material";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useState } from "react";
import { RhfControlledTableColumnDefinition } from "../../../Layout/RhfControlledTable/RhfControlledTableColumnDefinition";
import { defaultSprHandlingTime, SprHandlingTime } from "../../SprHandlingTime";
import { RhfControlledDataTable } from "../../../Layout/RhfControlledTable/RhfControlledDataTable";
import Odc from "../../Odc";
import { SprHandlingTimeFilterType } from "./HandlingTimeFilterType";
import { RhfControlledDataTableFilter } from "../../../Layout/RhfControlledTable/RhfControlledDataTableFilter";
import { isArrayUndefinedOrEmpty, mapKeyToArray } from "../../../Utils/arrays";
import { ResetFilters } from "../../../Layout/RhfControlledTable/ResetFilters";
import { mapReactHookFormIndizesToGivenFieldArray } from "../../../Utils/reactHookFormUtils";
import { plantUrls, supplierUrls } from "../../../URL/ApplicationUrls";
import { RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch } from "../../../Supplier/Form/RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch";
import { useFetchGeoLocationSelectionItems } from "../../../Locations/useFetchGeoLocationSelectionItems";
import { distinctStringItems } from "../../../Utils/strings";
import { RhfHandlingTimeTextField } from "./RhfHandlingTimeTextField";

export const SprHandlingTimesDataTable = () => {
  const { control, getValues } = useFormContext<Odc>();
  const { fields, prepend, remove } = useFieldArray<Odc>({
    control,
    name: "SprHandlingTimes",
  });

  const odcId = getValues("Id");
  const sprHandlingTimes = getValues("SprHandlingTimes");

  const { data: initialSupplierItems } = useFetchGeoLocationSelectionItems({
    apiUrl: supplierUrls.initialSelectableGsdbs.apiUrl,
    selectedItems: distinctStringItems(mapKeyToArray(sprHandlingTimes, "SupplierGsdb")),
    errorMessage: "Supplier",
    queryKeyName: "sprHandlingTime",
    queryKeySubName: "suppliers",
    id: odcId,
  });

  const { data: initialPlantItems } = useFetchGeoLocationSelectionItems({
    apiUrl: plantUrls.initialSelectableGsdbs.apiUrl,
    selectedItems: distinctStringItems(mapKeyToArray(sprHandlingTimes, "PlantGsdb")),
    errorMessage: "Plant",
    queryKeyName: "sprHandlingTime",
    queryKeySubName: "plants",
    id: odcId,
  });

  const sprHandlingTimesTableColumns: RhfControlledTableColumnDefinition<
    Odc,
    "SprHandlingTimes"
  >[] = [
    {
      fieldKey: "SupplierGsdb",
      isNumeric: false,
      headerText: "Supplier",
      width: "33%",
      render: (fieldName, disabled) => (
        <RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch
          control={control}
          name={fieldName}
          initialSelectionItems={initialSupplierItems || []}
          apiUrl={supplierUrls.selectableGsdbsNew.apiUrl}
          errorMessage="An error occurred when retrieving the Supplier GSDBs"
          disabled={disabled}
        />
      ),
    },
    {
      fieldKey: "PlantGsdb",
      isNumeric: false,
      headerText: "Plant",
      width: "33%",
      render: (fieldName, disabled) => (
        <RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch
          control={control}
          name={fieldName}
          initialSelectionItems={initialPlantItems || []}
          apiUrl={plantUrls.selectableGsdbsNew.apiUrl}
          errorMessage="An error occurred when retrieving the Plant GSDBs"
          disabled={disabled}
        />
      ),
    },
    {
      fieldKey: "HandlingTime",
      isNumeric: false,
      headerText: "Handling Time",
      width: "34%",
      render: (fieldName, disabled) => (
        <RhfHandlingTimeTextField name={fieldName} control={control} disabled={disabled} />
      ),
    },
  ];

  const [filteredFields, setFilteredFields] =
    useState<
      FieldArrayWithId<Odc, "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes">[]
    >();

  const filterSprHandlingTimes = (
    fieldArray: FieldArrayWithId<
      Odc,
      "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes"
    >[],
    propertyToFilterFor: SprHandlingTimeFilterType,
    searchValue: string
  ) => {
    const foundFields = fieldArray.filter((field) => {
      const sprHandlingTimeField = field as unknown as SprHandlingTime;

      if (propertyToFilterFor === "SupplierGsdb")
        return sprHandlingTimeField.SupplierGsdb.startsWith(searchValue);

      if (propertyToFilterFor === "PlantGsdb")
        return sprHandlingTimeField.PlantGsdb.startsWith(searchValue);

      if (propertyToFilterFor === "HandlingTime")
        return sprHandlingTimeField.HandlingTime.startsWith(searchValue);

      return [];
    });

    if (isArrayUndefinedOrEmpty(foundFields)) {
      setFilteredFields(undefined);
      return;
    }

    const filteredFieldsWithIndexRelationToRhfFieldArray = mapReactHookFormIndizesToGivenFieldArray(
      {
        fieldsFromFormContext: fields,
        filteredFields: foundFields,
      }
    );

    setFilteredFields(filteredFieldsWithIndexRelationToRhfFieldArray);
  };

  const [resetFilters, setResetFilters] = useState<boolean>(false);

  return (
    <>
      <Grid container direction="row" pl={2} mt={1} spacing={0.5}>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Supplier GSDB"
            handleSearch={(searchValue: string) =>
              filterSprHandlingTimes(fields, "SupplierGsdb", searchValue)
            }
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Plant GSDB"
            handleSearch={(searchValue: string) =>
              filterSprHandlingTimes(fields, "PlantGsdb", searchValue)
            }
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Handling Time"
            handleSearch={(searchValue: string) =>
              filterSprHandlingTimes(fields, "HandlingTime", searchValue)
            }
          />
        </Grid>
        <ResetFilters
          handleClick={() => {
            setFilteredFields(undefined);
            setResetFilters(!resetFilters);
          }}
        />
      </Grid>
      <Grid item xs={12} container direction="column" style={{ paddingTop: "4px" }}>
        <RhfControlledDataTable
          fieldArrayName="SprHandlingTimes"
          columns={sprHandlingTimesTableColumns}
          defaultForNewRow={defaultSprHandlingTime}
          rows={filteredFields ?? fields}
          insertRow={prepend}
          removeRow={remove}
        />
      </Grid>
    </>
  );
};
